<script lang="ts" setup>
import type { IconDefinition } from '@fortawesome/duotone-regular-svg-icons'
import type { Product, SupportProductName } from '~/models/Product'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faBooks, faGlasses, faHeadphones, faLampDesk, faPaperPlane } from '@fortawesome/duotone-regular-svg-icons'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useText from '~/composables/useText'
import useSubjectButton from '~/composables/useSubjectButton'
import { useStaticProduct } from '~/composables/useStaticProduct'
import useProductHelper from '~/composables/useProductHelper'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import SupportProductModal from '~/components/home/SupportProductModal.vue'

const { t } = useI18n()
const { capitalize } = useText()
const { getStyles } = useSubjectButton()
const { ALWAYS_SHOWN_PRODUCTS } = useStaticProduct()
const { resolveBaseUrl } = useUrlBuilder()
const { filterProductsByGradeType, isUnderDevelopment, isExams, isLibrary, isPaperPlus } = useProductHelper()
const { selectedGradeType } = storeToRefs(useAuthStore())
const {
  products,
  filteredSubjectCodes,
  selectedSupportProductName,
  filteredAddonProducts,
  filteredSupportProductNames,
  filteredSupportProducts,
  isLoading,
} = storeToRefs(useProductStore())

const underDevelopmentProduct = computed(() => products.value.find(product => isUnderDevelopment(product)))
const isHome8to10 = computed(() => selectedGradeType.value === GradeType.Grade8To10)
const productsForGrade = computed(() => selectedGradeType.value
  && ALWAYS_SHOWN_PRODUCTS.filter(filterProductsByGradeType(selectedGradeType.value)))

const getProductIcon = (product: Product) => {
  if (isPaperPlus(product))
    return faPaperPlane
  if (isLibrary(product))
    return faGlasses
  if (isExams(product))
    return faLampDesk
  return ''
}

const supportProductIcons: Record<SupportProductName, IconDefinition> = {
  bokstøtte: faBooks,
  lydstøtte: faHeadphones,
}

const flipSize = computed(() => isHome8to10.value ? '1rem' : '1.2rem')
</script>

<template>
  <AuSkeletonWrapper
    v-if="isLoading"
    class="space-y-4 space-x-4"
  >
    <AuSkeleton
      v-for="n in 16"
      :key="n"
      :width="`${Math.floor(Math.random() * 8) + 8}ch`"
      background="var(--color-green-30)"
      :height="isHome8to10 ? '2.5rem' : '3.25rem'"
      class="inline-block align-top"
    />
  </AuSkeletonWrapper>

  <ul v-else class="space-y-4 space-x-4">
    <template
      v-for="subject of filteredSubjectCodes"
      :key="subject"
    >
      <li class="inline-block align-top">
        <Button
          :class="isHome8to10 ? 'text-base' : 'px-4 py-3 text-xl'"
          :style="getStyles(subject)"
          :to="{ name: 'subject', params: { subject: subject.toLowerCase() } }"
          class="bg-seagreen-60 hover:not-disabled:text-seagreen-60 rounded-xl font-bold text-(--color) outline-(--color) hover:not-disabled:bg-(--color)"
          size="lg"
          variant="outline"
        >
          {{ t(`metadata.subjects.${subject}`) }}
        </Button>
      </li>
    </template>
    <li
      v-for="addon in filteredAddonProducts.filter(product => !isUnderDevelopment(product))"
      :key="addon.ean"
      class=" inline-block align-top"
    >
      <Button
        :class="isHome8to10 ? 'text-base' : 'px-4 py-3 text-xl'"
        :style="getStyles('white')"
        :to="{ path: resolveBaseUrl(addon) }"
        class="bg-seagreen-60 hover:not-disabled:text-seagreen-60 rounded-xl font-bold text-(--color) outline-(--color) hover:not-disabled:bg-(--color)"
        size="lg"
        variant="outline"
      >
        {{ addon.aunivers.name }}
      </Button>
    </li>
    <li
      v-for="product in productsForGrade"
      :key="product.ean"
      class=" inline-block align-top"
    >
      <Button
        :class="isHome8to10 ? 'text-base' : 'px-4 py-3 text-xl'"
        :style="getStyles(product.aunivers.name)"
        :to="{ path: resolveBaseUrl(product) }"
        class="bg-seagreen-60 hover:not-disabled:text-seagreen-60 rounded-xl font-bold text-(--color) outline-(--color) hover:not-disabled:bg-(--color)"
        size="lg"
        variant="outline"
      >
        <font-awesome-icon v-if="getProductIcon(product)" :icon="getProductIcon(product)" />
        {{ product.aunivers.name }}
      </Button>
    </li>
    <li
      v-for="name of filteredSupportProductNames"
      :key="name"
      class=" inline-block align-top"
    >
      <Button
        :class="isHome8to10 ? 'text-base' : 'px-4 py-3 text-xl'"
        :style="getStyles(name)"
        class="btn-flip-corner bg-seagreen-60 hover:not-disabled:text-seagreen-60 rounded-xl font-bold text-(--color) outline-(--color) hover:not-disabled:bg-(--color)"
        variant="outline"
        @click="selectedSupportProductName = name"
      >
        <font-awesome-icon :icon="supportProductIcons[name]" />
        {{ capitalize(name) }}
      </Button>
      <SupportProductModal
        :name="name"
        :products="filteredSupportProducts"
        :subjects="filteredSubjectCodes"
        @close="selectedSupportProductName = undefined"
      />
    </li>
    <li v-if="underDevelopmentProduct?.subjects?.length" class="">
      <router-link
        :to="{ name: 'subject', params: { subject: underDevelopmentProduct.subjects[0].toLowerCase() } }"
        class="hover:text-green-10 text-green-5 block py-2 underline underline-offset-2 focus-visible:ring"
      >
        {{ underDevelopmentProduct.aunivers.name }}
      </router-link>
    </li>
  </ul>
</template>

<style scoped>
.btn-flip-corner {
  position: relative;
  --flip-size: v-bind(flipSize);
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - var(--flip-size)), calc(100% - var(--flip-size)) 100%, 0 100%);
}
.btn-flip-corner::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  box-sizing: content-box;
  width: var(--flip-size);
  height: var(--flip-size);
  border-top: 1px solid var(--color-seagreen-60);
  border-left: 1px solid var(--color-seagreen-60);
  border-top-left-radius: inherit;
  background: var(--color);
}
.btn-flip-corner:hover {
  background: var(--color);
}
</style>
